import { useRef, useEffect } from 'react';

export const useAudioPlayer = () => {
  const initializedRef = useRef(false);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const analyserRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const audioDestinationRef = useRef(null);
  const isReceivingAudioRef = useRef(false);

  const start = async () => {
    if (initializedRef.current) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.ffSize = 512;

      const outputWorkletUrl = '/worklet/audioPlayerProcessor.worklet.js';
      await audioContext.audioWorklet.addModule(outputWorkletUrl);
      const workletNode = new AudioWorkletNode(audioContext, 'audio-player-processor');

      workletNode.port.onmessage = (event) => {
        if (event.data.type === 'speaking') {
          isReceivingAudioRef.current = event.data.value;
        }
      };

      const destination = audioContext.createMediaStreamDestination();

      workletNode.connect(analyser);
      analyser.connect(audioContext.destination);
      workletNode.connect(destination); // audio del backend al destino mezclado

      audioDestinationRef.current = destination; // NUEVO
      audioContextRef.current = audioContext;
      workletNodeRef.current = workletNode;
      analyserRef.current = analyser;
      initializedRef.current = true;

      console.log('AudioPlayer initialized successfully');
    } catch (err) {
      console.error('Failed to initialize audio player:', err);
    }
  };

  const stop = () => {
    if (!initializedRef.current) return;

    if (audioContextRef.current) audioContextRef.current.close();
    if (analyserRef.current) analyserRef.current.disconnect();
    if (workletNodeRef.current) workletNodeRef.current.disconnect();
    if (processorRef.current) processorRef.current.disconnect();
    if (sourceRef.current) sourceRef.current.disconnect();

    initializedRef.current = false;
    audioContextRef.current = null;
    analyserRef.current = null;
    workletNodeRef.current = null;
    processorRef.current = null;
    sourceRef.current = null;
    audioDestinationRef.current = null;
  };

  const playAudio = (samples) => {
    if (!initializedRef.current || !workletNodeRef.current) {
      console.error(
        'The audio player is not initialized. Call start() before attempting to play audio.'
      );
      return;
    }

    try {
      workletNodeRef.current.port.postMessage({
        type: 'audio',
        audioData: samples,
      });
    } catch (error) {
      console.error('Error sending audio to worklet:', error);
    }
  };

  const bargeIn = () => {
    if (!initializedRef.current || !workletNodeRef.current) return;
    workletNodeRef.current.port.postMessage({
      type: 'barge-in',
    });
  };

  const connectMicInput = (mediaStream, onAudioData) => {
    if (!audioContextRef.current) {
      console.warn('audioContextRef is not initialized');
      return;
    }

    const source = audioContextRef.current.createMediaStreamSource(mediaStream);
    const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);

    processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      onAudioData(new Float32Array(input));
    };

    source.connect(processor);
    processor.connect(audioContextRef.current.destination);
    source.connect(audioDestinationRef.current);

    sourceRef.current = source;
    processorRef.current = processor;
  };

  // Limpieza al desmontar
  useEffect(() => () => stop(), []);

  return {
    start,
    stop,
    playAudio,
    connectMicInput,
    bargeIn,
    isInitialized: initializedRef.current,
    getMixedAudioStream: () => audioDestinationRef.current?.stream,
    getSampleRate: () => audioContextRef.current?.sampleRate ?? 48000,
    isReceivingAudioRef,
  };
};
